import { useClickOutside } from '@mm/hooks';
import { ArrowStatefulIcon } from '@mm/icons';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Chip } from '../Chip/Chip';
import { Heading } from '../Heading/Heading';
import { TextLink } from '../TextLink/TextLink';
import { Button } from '../Button/Button';
import './AccordionItem.pcss';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';

type Props = {
  className?: string;
  icon?: React.ReactNode;
  count?: number;
  title?: string;
  titleComponent?: React.ReactNode;
  description?: React.ReactNode;
  descriptionClassName?: string;
  children: React.ReactNode;
  closeOnOutsideClick?: boolean;
  openOnMount?: boolean;
  arrowPosition?: 'left' | 'right';
  arrowDirectionVertical?: boolean;
  withBorder?: boolean;
  arrowClassName?: string;
  openArrowClassName?: string;
  headingClassName?: string;
  rightPanel?: React.ReactNode;
  onToggle?: (opened: boolean) => void;
  includeTooltip?: boolean;
  withHeaderButton?: boolean;
  withHeaderButtonText?: string;
  headerButtonCss?: {
    container?: string;
    leftIcon?: string;
  };
  onHeaderButtonClick?: any;
  childrenWrapperClassName?: string;
  headerWrapperClassName?: string;
  caretStyleIcon?: boolean;
};

export const AccordionItem = ({
  className,
  title,
  icon: Icon,
  count,
  description,
  descriptionClassName,
  children,
  closeOnOutsideClick = true,
  openOnMount = false,
  arrowPosition = 'right',
  arrowClassName,
  openArrowClassName,
  headingClassName,
  rightPanel,
  arrowDirectionVertical,
  withBorder = true,
  onToggle,
  titleComponent,
  includeTooltip,
  withHeaderButton,
  withHeaderButtonText,
  headerButtonCss,
  onHeaderButtonClick,
  childrenWrapperClassName,
  headerWrapperClassName,
  caretStyleIcon,
}: Props) => {
  const [isOpen, setIsOpen] = useState(openOnMount);
  const toggle = () => {
    onToggle?.(!isOpen);
    setIsOpen(!isOpen);
  };
  const close = () => setIsOpen(false);

  const onOutsideClick = () => {
    if (closeOnOutsideClick) {
      close();
    }
  };
  const ref = useClickOutside<HTMLDivElement>(onOutsideClick, includeTooltip);
  const onClick = () => (!isOpen ? toggle() : null);

  const arrowDirection = isOpen
    ? 'down'
    : arrowDirectionVertical
    ? 'up'
    : 'right';

  return (
    <div
      className={classNames(
        className,
        'c-accordion-item',
        `c-accordion-item--arrow-${arrowPosition}`,
        {
          'c-accordion-item--open': isOpen,
          'c-accordion-item--panels': !!rightPanel,
          'c-accordion-item--bordered': withBorder,
        },
      )}
      ref={ref}
      onClick={onClick}
    >
      <div className="c-accordion-item__item-wrapper">
        <div
          className={classNames(
            headerWrapperClassName,
            'c-accordion-item__item-header-wrapper',
          )}
        >
          <div className="c-accordion-item__item" onClick={toggle}>
            {Icon && <div className="c-accordion-item__icon">{Icon}</div>}
            {titleComponent ? (
              titleComponent
            ) : (
              <Heading
                className={classNames(
                  'c-accordion-item__heading',
                  headingClassName,
                )}
                size={4}
                text={title}
              />
            )}
            {count !== undefined && (
              <Chip
                type="neutral"
                className="c-accordion-item__count"
                text={String(count)}
              />
            )}
            {caretStyleIcon ? (
              isOpen ? (
                <CaretUpOutlined
                  className={classNames(
                    'c-accordion-item__arrow-icon-caret',
                    arrowClassName,
                  )}
                />
              ) : (
                <CaretDownOutlined
                  className={classNames(
                    'c-accordion-item__arrow-icon-caret',
                    arrowClassName,
                  )}
                />
              )
            ) : (
              <ArrowStatefulIcon
                className={classNames(
                  'c-accordion-item__arrow-icon',
                  arrowClassName,
                  isOpen ? openArrowClassName : undefined,
                )}
                color={isOpen ? '#3A50F3' : undefined}
                direction={arrowDirection}
              />
            )}
          </div>
          {description && (
            <TextLink className={descriptionClassName}>{description}</TextLink>
          )}
          {isOpen && withHeaderButton && withHeaderButtonText && (
            <Button
              css={headerButtonCss}
              color="blue"
              onClick={onHeaderButtonClick}
            >
              {withHeaderButtonText}
            </Button>
          )}
        </div>

        {isOpen && (
          <div
            className={classNames(
              'c-accordion-item__children-container',
              childrenWrapperClassName,
            )}
          >
            {children}
          </div>
        )}
      </div>
      {isOpen && rightPanel && (
        <div className="c-accordion-item__right-panel-container">
          {rightPanel}
        </div>
      )}
    </div>
  );
};
